/**
 * Tenant and Platform API Helpers
 *
 * Two distinct surfaces, deliberately kept apart:
 *
 *   /api/v1/tenants/*   — the caller's OWN tenant: its team, seats and invites. Any member
 *                         may read; changes require a tenant admin (Owner or Admin).
 *   /api/v1/platform/*  — Akhile only. The single place that reads across tenants, which is
 *                         what lets every other endpoint stay unconditionally tenant-scoped.
 *
 * Note no call here passes a tenant id for the caller's own tenant: the active tenant travels
 * in the JWT, so the client cannot ask for someone else's data by changing a parameter.
 */

import { fetcher } from '../utils/fetcher';
import type {
  AcceptInviteRequest,
  InviteRequest,
  LoginResponse,
  MembershipStatus,
  Tenant,
  TenantMember,
  TenantMembership,
  TenantRole,
  TenantWithSeats,
} from '../types/auth';

const TENANTS_URL = '/api/v1/tenants';
const PLATFORM_URL = '/api/v1/platform';
const AUTH_URL = '/api/v1/auth';

// ---------------------------------------------------------------------------
// The caller's own tenant
// ---------------------------------------------------------------------------

/** The active tenant with seat usage. Readable by any member — the counter belongs on the
 *  team page, and "8 of 10 seats used" is not privileged within a tenant. */
export const getCurrentTenant = async (): Promise<TenantWithSeats> =>
  await fetcher(`${TENANTS_URL}/current`);

export const getCurrentTenantMembers = async (): Promise<TenantMember[]> =>
  await fetcher(`${TENANTS_URL}/current/members`);

/** Invite someone by email. Returns 403 if no seat is free — the cap is hard. */
export const inviteToCurrentTenant = async (invite: InviteRequest): Promise<TenantMembership> =>
  await fetcher(`${TENANTS_URL}/current/invites`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(invite),
  });

export const updateCurrentTenantMember = async (
  membershipId: string,
  updates: { tenant_role?: TenantRole; status?: MembershipStatus }
): Promise<TenantMembership> =>
  await fetcher(`${TENANTS_URL}/current/members/${membershipId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });

/** Remove someone from this tenant. Deletes the membership, not the person: their login and
 *  any access in other tenants are untouched. */
export const removeCurrentTenantMember = async (membershipId: string): Promise<void> => {
  await fetcher(`${TENANTS_URL}/current/members/${membershipId}`, { method: 'DELETE' });
};

// ---------------------------------------------------------------------------
// Tenant switching (one login, several tenants)
// ---------------------------------------------------------------------------

/** Available without a tenant context, so someone whose active tenant was revoked can still
 *  discover where they do have access. */
export const getMyMemberships = async (): Promise<TenantMembership[]> =>
  await fetcher(`${AUTH_URL}/memberships`);

/** Returns a NEW token scoped to the target tenant; the caller must replace the one it holds. */
export const switchTenant = async (tenantId: string): Promise<LoginResponse> =>
  await fetcher(`${AUTH_URL}/switch-tenant`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenant_id: tenantId }),
  });

/** Unauthenticated: the emailed token is the proof of identity, as for a password reset. */
export const acceptInvite = async (data: AcceptInviteRequest): Promise<{ message: string }> =>
  await fetcher(`${AUTH_URL}/accept-invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

// ---------------------------------------------------------------------------
// Platform (Akhile) — the only cross-tenant surface
// ---------------------------------------------------------------------------

export const listTenants = async (): Promise<TenantWithSeats[]> =>
  await fetcher(`${PLATFORM_URL}/tenants`);

/** Returns the bare tenant: the create endpoint answers with no seat counts, and a new
 *  workspace has no members to count anyway. Re-read if you need seat usage. */
export const createTenant = async (
  data: Pick<Tenant, 'name' | 'slug' | 'seats_purchased'>
): Promise<Tenant> =>
  await fetcher(`${PLATFORM_URL}/tenants`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

export const getTenant = async (tenantId: string): Promise<TenantWithSeats> =>
  await fetcher(`${PLATFORM_URL}/tenants/${tenantId}`);

/** Lowering seats below current usage is allowed and evicts nobody — the tenant simply cannot
 *  add more until they are back under the limit.
 *
 *  Returns the bare tenant, without seat counts — so splice it over the row you already hold
 *  rather than replacing that row, or the seat figures become undefined. */
export const updateTenant = async (
  tenantId: string,
  updates: Partial<Pick<Tenant, 'name' | 'status' | 'seats_purchased'>>
): Promise<Tenant> =>
  await fetcher(`${PLATFORM_URL}/tenants/${tenantId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });

export const listTenantMembers = async (tenantId: string): Promise<TenantMember[]> =>
  await fetcher(`${PLATFORM_URL}/tenants/${tenantId}/members`);

/** How a new client gets its first Owner. Pass seatCounted=false for Akhile staff joining a
 *  client tenant, so the client is not billed for our people. */
export const addTenantMember = async (
  tenantId: string,
  email: string,
  tenantRole: TenantRole = 'Owner',
  seatCounted = true
): Promise<TenantMembership> => {
  const params = new URLSearchParams({
    email,
    tenant_role: tenantRole,
    seat_counted: String(seatCounted),
  });
  return await fetcher(`${PLATFORM_URL}/tenants/${tenantId}/members?${params}`, {
    method: 'POST',
  });
};
